import React, { createContext, useContext, useEffect, useState } from "react";
import { EnvContext } from "./EnvContext"; // Where your EnvContext is defined
import config from "../config";
import { collection, getDocs, doc, getDoc, orderBy, query, limit } from "firebase/firestore";
import { useFirestore } from "../firebase/FirestoreContext";

export const SystemContext = createContext(null);

export const SystemContextProvider = ({ children }) => {
  const environment = useContext(EnvContext);
  const [isMounted, setIsMounted] = useState(false);

  // States for storing the fetched data
  const [productVibes, setProductVibes] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [productTags, setProductTags] = useState([]);
  const [shipmentSettings, setShipmentSettings] = useState(null); // new state

  // Loading state if you want to display a spinner while fetching
  const [loadingSystem, setLoadingSystem] = useState(true);

  // Pick the correct path based on environment
  const collectionPath =
    environment === "staging"
      ? config.environment.staging.collectionPath
      : config.environment.production.collectionPath;
  const firestore = useFirestore();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return () => {};

    const fetchSystemData = async () => {
      setLoadingSystem(true);
      const catSnap = await getDocs(
        collection(firestore, `SooperstockSettings/ProductCategories/DataBase`),
      );
      const categories = catSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch product vibes
      const vibeSnap = await getDocs(
        collection(
          firestore,
          `${collectionPath}SooperstockSettings/ProductVibes/DataBase`,
        ),
      );
      const vibes = vibeSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch product tags
      const tagSnap = await getDocs(
        collection(
          firestore,
          `${collectionPath}SooperstockSettings/ProductTags/DataBase`,
        ),
      );
      const tags = tagSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch shipment settings - keep perishablePrice from original source
      const shipmentDoc = await getDoc(
        doc(firestore, `${collectionPath}SooperstockSettings/Shipment`),
      );
      const originalShipmentData = shipmentDoc.data();
      
      // Fetch cheapest shipping option from ShippingOptions table
      const shippingOptionsQuery = query(
        collection(firestore, `${collectionPath}ShippingOptions/ShippingOptionsList/DataBase`),
        orderBy("price", "asc"),
        limit(1)
      );
      const shippingSnapshot = await getDocs(shippingOptionsQuery);
      
      let shipmentData = originalShipmentData;
      
      // If we have shipping options, use the cheapest one for threshold and price
      if (!shippingSnapshot.empty) {
        const cheapestOption = shippingSnapshot.docs[0].data();
        shipmentData = {
          perishablePrice: originalShipmentData?.perishablePrice || 0,
          freeShipmentThreshold: cheapestOption.free_threshold || 0,
          shipmentPrice: cheapestOption.price || 0,
        };
      }

      // Update state if still mounted
      if (isMounted) {
        setProductCategories(categories);
        setProductVibes(vibes);
        setProductTags(tags);
        setShipmentSettings(shipmentData); // set shipment settings
        setLoadingSystem(false);
      }
    };

    fetchSystemData();
  }, [collectionPath, firestore, isMounted]);

  console.log(shipmentSettings);
  return (
    <SystemContext.Provider
      value={{
        productVibes,
        productCategories,
        productTags,
        loadingSystem,
        shipmentSettings, // expose shipment settings
      }}
    >
      {children}
    </SystemContext.Provider>
  );
};
